/**
 * Hand-off from the deck builder to UCG Rising (the playable app).
 *
 * Rising already accepts our deck codes — its import box detects a base64 code
 * and resolves it through our own `/api/deck/decode`. This module just skips the
 * copy-paste: it puts the code in a `?deck=` query param that Rising reads on
 * boot (see `consumeDeckDeepLink` there) and opens straight into its deck
 * builder with the deck loaded.
 *
 * The clipboard copy is not a nicety — it's the fallback. If the popup is
 * blocked, or the player is signed out and bails at Rising's login, the code is
 * already on their clipboard and the manual Import path still works.
 */

/** Where Rising is served. */
export const RISING_ORIGIN = 'https://ucgrising.com';

/** Query param Rising reads the deck code from. Keep in sync with its DECK_PARAM. */
export const RISING_DECK_PARAM = 'deck';

/**
 * Encode deck cards into a v2 deck code.
 *
 * This is the same base64(JSON) form the Export Deck Code dialog produces —
 * `{version, cards:[{id, count}]}` keyed on Cosmos card ids — so a code from
 * either path decodes identically.
 *
 * @param {Array<{id: string, count: number}>} cards
 * @returns {string} base64 deck code
 */
export function encodeDeckCode(cards) {
  const deckData = {
    version: 2,
    cards: cards.map((card) => ({ id: card.id, count: card.count })),
  };
  // unescape(encodeURIComponent(...)) makes btoa safe for non-Latin-1 content
  // (localized card data can carry it), matching the existing export path.
  return btoa(unescape(encodeURIComponent(JSON.stringify(deckData))));
}

/**
 * Build the Rising deep link for a deck code.
 *
 * @param {string} deckCode
 * @returns {string} absolute URL
 */
export function buildRisingDeckUrl(deckCode) {
  const params = new URLSearchParams({ [RISING_DECK_PARAM]: deckCode });
  return `${RISING_ORIGIN}/?${params.toString()}`;
}

/**
 * Copy text to the clipboard, tolerating the absence of the async Clipboard API
 * (older Safari, and any non-secure context — where it is simply undefined).
 *
 * @returns {Promise<boolean>} whether the copy landed
 */
export async function copyToClipboard(text) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Permission denied or a non-secure context — fall through to execCommand.
  }

  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    // Keep it out of view and out of the tab order, but still selectable.
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
}

/**
 * Send a deck to Rising: encode it, copy the code as a fallback, and open the
 * deep link in a new tab.
 *
 * @param {Array<{id: string, count: number}>} cards
 * @returns {Promise<{deckCode: string, url: string, opened: boolean, copied: boolean}>}
 */
export async function sendDeckToRising(cards) {
  const deckCode = encodeDeckCode(cards);
  const url = buildRisingDeckUrl(deckCode);

  const copied = await copyToClipboard(deckCode);

  // noopener/noreferrer: the new tab must not get a handle on this window.
  const win = window.open(url, '_blank', 'noopener,noreferrer');
  const opened = !!win;

  return { deckCode, url, opened, copied };
}
